import React, { useState } from 'react';
import { fmtInt, fmtRate, fmtDay } from '../../lib/slotStatsView';
import { Delta, ScrollX } from './ChartKit';
import { cn } from '@/lib/utils';

/*
 * Round 14b: the Social-Blade-style daily table - one row per day, newest first: followers,
 * following and posts (the count at that day's profile check and the change since the previous
 * check) and the day's actions, with the range's total change and daily average in the footer.
 * Capped at the selected range; the first 14 rows show until "Show all" is pressed. On a phone the
 * date column stays put and the table says it scrolls sideways.
 */

export const DAILY_TABLE_PAGE = 14;

const GROWTH_COLS = [
  { key: 'followers', label: 'Followers' },
  { key: 'followings', label: 'Following' },
  { key: 'posts', label: 'Posts' },
];

function GrowthCell({ v }) {
  if (v.change === null && v.count === null) return <td className="px-3 py-2 text-right text-muted-foreground">-</td>;
  return (
    <td className="whitespace-nowrap px-3 py-2 text-right">
      <span className="inline-flex items-baseline justify-end gap-2">
        {v.count !== null && <span className="text-foreground">{fmtInt(v.count)}</span>}
        {v.change === null
          ? <span className="min-w-[3.5rem] text-xs text-muted-foreground">-</span>
          : <Delta value={v.change} className="min-w-[3.5rem] justify-end text-xs text-muted-foreground" />}
      </span>
    </td>
  );
}

/** table: dailyTable() result. */
export default function DailyTable({ table, range }) {
  const [all, setAll] = useState(false);
  const { rows, summary } = table;
  const shown = all ? rows : rows.slice(0, DAILY_TABLE_PAGE);
  return (
    <div>
      <ScrollX testid="growth-daily-scroll">
        <table className="w-full min-w-[34rem] text-sm tnum" data-testid="growth-daily">
          <caption className="sr-only">Daily history, newest first: followers, following, posts and actions per day</caption>
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="viz-sticky-col px-3 py-2 font-medium">Date</th>
              {GROWTH_COLS.map((c) => (
                <th key={c.key} scope="col" className="px-3 py-2 text-right font-medium">{c.label}</th>
              ))}
              <th scope="col" className="px-3 py-2 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.date} className="border-t border-white/5" data-testid={`growth-day-${r.date}`}>
                <th scope="row" className="viz-sticky-col whitespace-nowrap px-3 py-2 text-left font-normal text-muted-foreground">
                  {/* the year only where there is room: a phone keeps the numbers in view */}
                  {fmtDay(r.date, 'long').replace(/ \d{4}$/, '')}
                  <span className="hidden sm:inline">{` ${r.date.slice(0, 4)}`}</span>
                  {r.isToday ? <span className="ml-1.5 text-[11px] text-primary">today</span> : null}
                </th>
                {GROWTH_COLS.map((c) => <GrowthCell key={c.key} v={r[c.key]} />)}
                <td className={cn('whitespace-nowrap px-3 py-2 text-right', r.actions === null ? 'text-muted-foreground' : 'text-foreground')}>
                  {fmtInt(r.actions)}
                  {r.partial && r.actions !== null && <span className="ml-1 text-[11px] text-muted-foreground">so far</span>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-white/15 text-xs">
            <tr data-testid="daily-total">
              <th scope="row" className="viz-sticky-col px-3 py-2 text-left font-medium text-foreground">Change over {rows.length} day{rows.length === 1 ? '' : 's'}</th>
              {GROWTH_COLS.map((c) => (
                <td key={c.key} className="whitespace-nowrap px-3 py-2 text-right font-semibold text-foreground">
                  {summary.change[c.key] === null ? '-' : <Delta value={summary.change[c.key]} className="justify-end" />}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-foreground">{fmtInt(summary.actions.total)}</td>
            </tr>
            <tr data-testid="daily-average">
              <th scope="row" className="viz-sticky-col px-3 py-2 text-left font-medium text-foreground">Daily average</th>
              {GROWTH_COLS.map((c) => (
                <td key={c.key} className="px-3 py-2 text-right text-muted-foreground">{fmtRate(summary.average[c.key])}</td>
              ))}
              <td className="px-3 py-2 text-right text-muted-foreground">
                {fmtRate(summary.actions.average, { signed: false })}{summary.actions.partial ? '*' : ''}
              </td>
            </tr>
          </tfoot>
        </table>
      </ScrollX>
      {summary.actions.partial && (
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="daily-average-note">
          * Today&apos;s actions are a day in progress: counted in the total, left out of the daily average.
        </p>
      )}
      {rows.length > DAILY_TABLE_PAGE && (
        <button type="button" onClick={() => setAll((v) => !v)} data-testid="daily-table-more"
          className="tap mt-3 inline-flex h-8 items-center rounded-full border border-white/10 px-3.5 text-xs font-medium text-muted-foreground hover:bg-white/5 hover:text-foreground">
          {all ? `Show the latest ${DAILY_TABLE_PAGE} days` : `Show all ${rows.length} days of the ${range}-day range`}
        </button>
      )}
    </div>
  );
}
